// ── Browser storage ───────────────────────────────────────────────────────────
// Single source of truth for localStorage / sessionStorage keys, with safe
// accessors (quota- and parse-error tolerant). Import `storage` instead of
// touching window storage directly, so keys and (de)serialization live in one
// place.

const raw = (key) => {
  try { return localStorage.getItem(key) } catch { return null }
}
const write = (key, value) => {
  try { localStorage.setItem(key, value) } catch { /* quota / private mode */ }
}
const readJSON = (key, fallback) => {
  try {
    const v = localStorage.getItem(key)
    return v == null ? fallback : JSON.parse(v)
  } catch { return fallback }
}
const writeJSON = (key, value) => write(key, JSON.stringify(value))

export const storage = {
  // ── Language ('ua' | 'en') ────────────────────────────────────────────────
  language: {
    raw: () => raw('userLanguage'),
    get: () => raw('userLanguage') || 'ua',
    isEnglish: () => raw('userLanguage') === 'en',
    set: (lang) => write('userLanguage', lang),
  },

  // ── Theme ('dark' | 'light' | null) ───────────────────────────────────────
  theme: {
    get: () => raw('theme'),
    set: (value) => write('theme', value),
  },

  // ── Bible reader ──────────────────────────────────────────────────────────
  bibleLastPos: {
    get: () => readJSON('bibleLastPos', null),
    set: (pos) => writeJSON('bibleLastPos', pos),
  },
  bibleBookmarks: {
    get: () => readJSON('bibleBookmarks', []),
    set: (list) => writeJSON('bibleBookmarks', list),
  },
  bibleFontSize: {
    get: () => Number(raw('bibleFontSize')) || 16,
    set: (size) => write('bibleFontSize', String(size)),
  },
  bibleParallel: {
    get: () => raw('bibleParallel') === '1',
    set: (on) => write('bibleParallel', on ? '1' : '0'),
  },
  // Per-day "counted this reader" flag (sessionStorage).
  bibleReadMark: {
    has: (date) => { try { return !!sessionStorage.getItem(`bible_read_${date}`) } catch { return false } },
    set: (date) => { try { sessionStorage.setItem(`bible_read_${date}`, '1') } catch { /* ignore */ } },
  },

  // ── Bible quiz ────────────────────────────────────────────────────────────
  quizHistory: {
    get: () => readJSON('quizHistory_v1', []) || [],
    set: (history) => writeJSON('quizHistory_v1', history),
  },
  quizStatsSent: {
    get: () => raw('quizStatsSent_v1'),
    set: (date) => write('quizStatsSent_v1', date),
  },
  quizDailyCache: {
    get: () => readJSON('quizDailyCache_v2', null),
    set: (entry) => writeJSON('quizDailyCache_v2', entry),
  },
}

export default storage
